'use client'

import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react'

// Default terminology values (duplicated from tenant.ts to avoid server import)
const defaultTerminology: Record<string, string> = {
    member: 'Member',
    members: 'Members',
    household: 'Household',
    households: 'Households',
    yahrzeit: 'Yahrzeit',
    yahrzeits: 'Yahrzeits',
    congregation: 'Congregation',
    congregant: 'Congregant',
    congregants: 'Congregants',
}

type TerminologyContextType = {
    t: (key: string) => string
    terminology: Record<string, string>
}

const TerminologyContext = createContext<TerminologyContextType | null>(null)

export function useTerminology() {
    const context = useContext(TerminologyContext)
    if (!context) {
        throw new Error('useTerminology must be used within a TerminologyProvider')
    }
    return context
}

type TerminologyProviderProps = {
    children: ReactNode
    terminology: Record<string, string> | null
}

export function TerminologyProvider({ children, terminology }: TerminologyProviderProps) {
    const mergedTerminology = useMemo(
        () => ({ ...defaultTerminology, ...terminology }),
        [terminology]
    )

    const t = useCallback(
        (key: string) => mergedTerminology[key] ?? defaultTerminology[key] ?? key,
        [mergedTerminology]
    )

    return (
        <TerminologyContext.Provider value={{ t, terminology: mergedTerminology }}>
            {children}
        </TerminologyContext.Provider>
    )
}
